import { OPENING_AREA_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/openingAreaBackdrops';
import { forestBiome } from '../biomeArchetypes';

/** village_arrival — "The First Hearth".
 *
 * A moonlit refuge, not a daylight reset. Cold indigo sky and near-black
 * village silhouettes carry the tomb's unease outdoors; three localized amber
 * hearth pools keep NPCs/readability warm enough to invite the player forward.
 * Sparse layers preserve the on-ramp's modest render budget.
 */
export const VILLAGE_ARRIVAL = forestBiome({
  id: 'village_arrival',
  sky: { from: 0x0d1228, to: 0x30233a, bands: 6 },
  celestial: {
    atX: 0.78,
    atY: 0.16,
    color: 0xcbd8ff,
    haloColor: 0x879bd0,
    size: 58,
    haloScale: 3,
    coreAlpha: 0.76,
    haloAlpha: 0.13,
  },
  cloudBands: {
    count: 3,
    color: 0x697086,
    sizeMin: 380,
    sizeMax: 720,
    alphaMin: 0.07,
    alphaMax: 0.16,
    driftPxPerSec: 4,
    bandYMin: 0.08,
    bandYMax: 0.32,
  },
  motes: {
    colors: [0x9fb4e8, 0xffb45c],
    count: 12,
    sizeMin: 2,
    sizeMax: 5,
    driftPx: 18,
    periodMinMs: 12000,
    periodMaxMs: 22000,
    alphaMin: 0.12,
    alphaMax: 0.42,
  },
  rays: {
    color: 0x9fb4e8,
    count: 2,
    widthMin: 80,
    widthMax: 150,
    height: 520,
    tiltDeg: -8,
    periodMinMs: 15000,
    periodMaxMs: 24000,
    alphaMin: 0.03,
    alphaMax: 0.09,
  },
  glow: {
    blobs: [
      { color: 0xff7a38, atX: 0.32, atY: 0.95, size: 300, alpha: 0.22 },
      { color: 0xff9b52, atX: 0.5, atY: 0.96, size: 340, alpha: 0.18 },
      { color: 0xff7a38, atX: 0.68, atY: 0.95, size: 300, alpha: 0.22 },
      { color: 0x6f86bc, atX: 0.78, atY: 0.14, size: 260, alpha: 0.05 },
    ],
    breathe: { periodMs: 3200, min: 0.62, max: 1.08 },
  },
  lightField: {
    palette: [0xffa45c, 0x8196c8],
    count: 5,
    sizeMin: 100,
    sizeMax: 200,
    alphaMin: 0.03,
    alphaMax: 0.08,
    driftRadius: 40,
    periodMinMs: 12000,
    periodMaxMs: 20000,
  },
  weather: {
    mode: 'fall',
    color: 0x7f8298,
    colorEnd: 0x34354a,
    size: 3,
    frequencyMs: 720,
    lifespanMs: 3600,
    speed: 12,
    gravityY: 3,
    windX: 22,
    alphaEnd: 0.18,
    gust: { periodMs: 11000, min: 0.55, max: 1.15, phase: 0.3 },
  },
  // Separate hills, inhabited roofline and a low orchard edge keep NPCs legible.
  parallax: [
    { svg: OPENING_AREA_BACKDROPS.hearth.far, shape: 'spires', color: 0x252d43, speed: 0.02, height: 330, alpha: 0.52, detail: 0.45, sway: { ampPx: 5, periodMs: 40000 } },
    { svg: OPENING_AREA_BACKDROPS.hearth.mid, shape: 'spires', color: 0x1b2235, speed: 0.04, height: 245, alpha: 0.7, detail: 0.65 },
    { svg: OPENING_AREA_BACKDROPS.hearth.near, shape: 'spires', color: 0x131c2c, speed: 0.08, height: 110, alpha: 0.68, detail: 0.8 },
  ],
  extraLayers: [
    // A few high silhouettes keep the sky alive without turning it busy.
    {
      kind: 'critters',
      count: 3,
      palette: [0x15192a, 0x252a3d],
      sizeMin: 4,
      sizeMax: 7,
      speedMin: 18,
      speedMax: 36,
      bandYMin: 0.08,
      bandYMax: 0.25,
      direction: 'both',
    },
  ],
  closer: {
    vignette: { color: 0x050711, edgeAlpha: 0.32, washColor: 0x151225, washAlpha: 0.04 },
    grade: { top: 0x526080, bottom: 0x241c2d, strength: 0.12, bands: 4, blend: 'normal' },
  },
});
